import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import EmptyAddCardCst from "@components/EmptyAddCardCst";
import ItemCardCst from "@components/ItemCardCst";
import MediaLibraryPickerCst from "@components/MediaLibraryPickerCst";
import { MediaType } from "src/interfaces/Media";
import { AssociationItem } from "../../../types";

interface MultiAssociationFieldProps {
  label: string;
  values: AssociationItem[];
  onChange: (next: AssociationItem[]) => void;
  addLabel: string;
  emptyLabel?: string;
  emptyVariant?: "inline" | "card";
  mediaType?: MediaType;
  pickerTitle?: string;
  onAddClick?: () => void;
}

const MultiAssociationField: React.FC<MultiAssociationFieldProps> = ({
  label,
  values,
  onChange,
  addLabel,
  emptyLabel,
  emptyVariant = "inline",
  mediaType,
  pickerTitle,
  onAddClick,
}) => {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);

  const isCardVariant = emptyVariant === "card";

  const handleAdd = () => {
    if (onAddClick) {
      onAddClick();
      return;
    }
    if (mediaType) {
      setPickerOpen(true);
      return;
    }
    // TODO: fallback mock per associazioni senza picker dedicato
    // (certificati, badge — endpoint diversi).
    const fake: AssociationItem = {
      id: Date.now(),
      label: `${label} ${new Date().toLocaleTimeString()}`,
    };
    onChange([...values, fake]);
  };

  const handleRemove = (idx: number) => {
    const next = values.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {label}
        </Typography>
        {!isCardVariant && (
          <Box
            onClick={handleAdd}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              color: theme.palette.primary.main,
              fontSize: "13px",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {addLabel}
            <Plus size={14} />
          </Box>
        )}
      </Box>

      {isCardVariant ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {values.map((item, idx) => (
            <ItemCardCst
              key={`${item.id}-${idx}`}
              title={item.label || item.name || `#${item.id}`}
              segments={item.id ? [`ID: ${item.id}`] : []}
              onDelete={() => handleRemove(idx)}
            />
          ))}
          <EmptyAddCardCst
            addLabel={addLabel}
            onAdd={handleAdd}
            minHeight={values.length > 0 ? "64px" : "120px"}
          />
        </Box>
      ) : values.length === 0 ? (
        <Box
          sx={{
            padding: "12px 16px",
            minHeight: "44px",
            display: "flex",
            alignItems: "center",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            fontSize: "13px",
          }}
        >
          {emptyLabel}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {values.map((item, idx) => (
            <Box
              key={`${item.id}-${idx}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              <Typography sx={{ fontSize: "13px" }}>
                {item.label || item.name || `#${item.id}`}
                {item.id && (
                  <Box
                    component="span"
                    sx={{
                      color: theme.palette.text.tertiary,
                      ml: 1,
                    }}
                  >
                    {`| ID: ${item.id}`}
                  </Box>
                )}
              </Typography>
              <Box
                onClick={() => handleRemove(idx)}
                sx={{
                  cursor: "pointer",
                  color: theme.palette.text.tertiary,
                  display: "flex",
                  "&:hover": { color: theme.palette.error.main },
                }}
              >
                <Trash2 size={16} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {mediaType && (
        <MediaLibraryPickerCst
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          title={pickerTitle || label}
          mediaType={mediaType}
          multiple={true}
          initialSelection={values.map((v) => ({
            id: Number(v.id),
            label: v.label || v.name || `#${v.id}`,
          }))}
          onConfirm={(selection) => onChange(selection)}
        />
      )}
    </Box>
  );
};

export default MultiAssociationField;
